import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Loader2, MoreHorizontal, CalendarClock, CreditCard, KeyRound, Mail,
  Ban, ShieldCheck, Trash2, CheckCircle2, Search, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminUI";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const PLANS = ["starter", "classic", "enterprise", "unlimited"];

function fmtDate(v) {
  if (!v) return "—";
  try {
    return format(new Date(v), "dd MMM yyyy");
  } catch {
    return "—";
  }
}

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [templates, setTemplates] = useState([]);

  // dialog state
  const [planTarget, setPlanTarget] = useState(null);
  const [planValue, setPlanValue] = useState("starter");
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendDate, setExtendDate] = useState(null);
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailTemplate, setEmailTemplate] = useState("");
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => setUsers(false));
  useEffect(() => {
    load();
    api.get("/admin/email-templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);

  const patchUser = (id, patch) =>
    setUsers((list) => list.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const filtered = useMemo(() => {
    if (!Array.isArray(users)) return [];
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) => (u.email || "").toLowerCase().includes(t) || (u.name || "").toLowerCase().includes(t));
  }, [users, q]);

  const toggleSuspend = async (u) => {
    const action = u.status === "suspended" ? "unsuspend" : "suspend";
    try {
      await api.post(`/admin/users/${u.id}/${action}`);
      patchUser(u.id, { status: action === "suspend" ? "suspended" : "active" });
      toast.success(`Access ${action === "suspend" ? "revoked" : "restored"}`);
    } catch {
      toast.error("Action failed");
    }
  };

  const sendReset = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/send-reset`);
      toast.success(data.message || "Reset link sent");
    } catch {
      toast.error("Couldn't send the reset email. Note: sandbox email can't deliver to @example.com addresses.");
    }
  };

  const savePlan = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${planTarget.id}/plan`, { plan: planValue });
      patchUser(planTarget.id, { plan: planValue });
      toast.success("Plan updated");
      setPlanTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to change plan");
    } finally {
      setBusy(false);
    }
  };

  const saveExtend = async () => {
    if (!extendDate) {
      toast.error("Pick an end date");
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(extendDate).toISOString();
      const { data } = await api.patch(`/admin/users/${extendTarget.id}/extend`, { end_date: iso });
      patchUser(extendTarget.id, { trial_ends_at: data.trial_ends_at });
      toast.success("License extended");
      setExtendTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to extend");
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async () => {
    if (!emailTemplate) {
      toast.error("Choose a template");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${emailTarget.id}/send-email`, { template: emailTemplate });
      toast.success(data.message || "Email sent");
      setEmailTarget(null);
      setEmailTemplate("");
    } catch {
      toast.error("Couldn't send the email. Note: sandbox email can't deliver to @example.com addresses.");
    } finally {
      setBusy(false);
    }
  };

  const confirmPromote = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/users/${promoteTarget.id}/promote`);
      patchUser(promoteTarget.id, { role: "superadmin" });
      toast.success("User promoted to super-admin");
      setPromoteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to promote");
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      setUsers((list) => list.filter((x) => x.id !== deleteTarget.id));
      toast.success("User deleted");
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
    } finally {
      setBusy(false);
    }
  };

  if (users === null)
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  if (users === false) return <p className="text-sm text-destructive">Failed to load users.</p>;

  return (
    <div data-testid="admin-users">
      <PageHeader title="Users" subtitle={`${users.length} accounts`} />

      <div className="mb-4 flex items-center gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by email or name…"
            className="h-10 pl-9"
            data-testid="admin-users-search"
          />
        </div>
        <span className="text-xs text-muted-foreground">{filtered.length} shown</span>
      </div>

      <div className="rounded-2xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Jobs</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>License ends</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="font-medium">{u.email}</div>
                  <div className="text-xs text-muted-foreground">{u.name || "—"}</div>
                </TableCell>
                <TableCell><Badge variant="secondary" className="uppercase">{u.plan}</Badge></TableCell>
                <TableCell>
                  <Badge
                    variant={u.status === "suspended" ? "destructive" : "outline"}
                    className="capitalize"
                    data-testid={`user-status-${u.id}`}
                  >
                    {u.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-center tabular-nums">{u.jobs ?? 0}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                <TableCell className="text-muted-foreground">{fmtDate(u.trial_ends_at)}</TableCell>
                <TableCell>
                  <span className={`text-xs font-medium ${u.role === "superadmin" ? "text-primary" : "text-muted-foreground"}`}>
                    {u.role === "superadmin" ? "super-admin" : "user"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1.5" data-testid={`user-actions-${u.id}`}>
                        Actions <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem
                        onClick={() => { setExtendTarget(u); setExtendDate(u.trial_ends_at ? new Date(u.trial_ends_at) : new Date()); }}
                        data-testid={`user-action-extend-${u.id}`}
                      >
                        <CalendarClock className="mr-2 h-4 w-4" /> Extend license
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => { setPlanTarget(u); setPlanValue(u.plan || "starter"); }}
                        data-testid={`user-action-plan-${u.id}`}
                      >
                        <CreditCard className="mr-2 h-4 w-4" /> Change plan
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => sendReset(u)} data-testid={`user-action-reset-${u.id}`}>
                        <KeyRound className="mr-2 h-4 w-4" /> Reset password
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => { setEmailTarget(u); setEmailTemplate(""); }}
                        data-testid={`user-action-email-${u.id}`}
                      >
                        <Mail className="mr-2 h-4 w-4" /> Send email
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => toggleSuspend(u)} data-testid={`user-action-suspend-${u.id}`}>
                        {u.status === "suspended"
                          ? <><RotateCcw className="mr-2 h-4 w-4" /> Restore access</>
                          : <><Ban className="mr-2 h-4 w-4" /> Revoke access</>}
                      </DropdownMenuItem>
                      {u.role !== "superadmin" && (
                        <DropdownMenuItem onClick={() => setPromoteTarget(u)} data-testid={`user-action-promote-${u.id}`}>
                          <ShieldCheck className="mr-2 h-4 w-4 text-amber-500" /> Promote to super-admin
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => setDeleteTarget(u)}
                        className="text-destructive focus:text-destructive"
                        data-testid={`user-action-delete-${u.id}`}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Delete user…
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Change plan dialog */}
      <Dialog open={!!planTarget} onOpenChange={(o) => !o && setPlanTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="change-plan-dialog">
          <DialogHeader>
            <DialogTitle>Change plan</DialogTitle>
            <DialogDescription>{planTarget?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Plan</Label>
            <Select value={planValue} onValueChange={setPlanValue}>
              <SelectTrigger data-testid="change-plan-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLANS.map((p) => (
                  <SelectItem key={p} value={p} className="capitalize" data-testid={`plan-opt-${p}`}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPlanTarget(null)}>Cancel</Button>
            <Button onClick={savePlan} disabled={busy} data-testid="change-plan-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend license dialog */}
      <Dialog open={!!extendTarget} onOpenChange={(o) => !o && setExtendTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="extend-license-dialog">
          <DialogHeader>
            <DialogTitle>Extend license</DialogTitle>
            <DialogDescription>Pick the new end date for {extendTarget?.email}.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start" data-testid="extend-date-trigger">
                  <CalendarClock className="mr-2 h-4 w-4" />
                  {extendDate ? format(new Date(extendDate), "dd MMM yyyy") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={extendDate}
                  onSelect={setExtendDate}
                  initialFocus
                  data-testid="extend-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setExtendTarget(null)}>Cancel</Button>
            <Button onClick={saveExtend} disabled={busy} data-testid="extend-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save date"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send email dialog */}
      <Dialog open={!!emailTarget} onOpenChange={(o) => !o && setEmailTarget(null)}>
        <DialogContent className="max-w-sm" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle>Send email</DialogTitle>
            <DialogDescription>Send a template email to {emailTarget?.email}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Template</Label>
            <Select value={emailTemplate} onValueChange={setEmailTemplate}>
              <SelectTrigger data-testid="send-email-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
              <SelectContent>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`email-tpl-${t.id}`}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button onClick={sendEmail} disabled={busy} data-testid="send-email-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote confirm */}
      <AlertDialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)}>
        <AlertDialogContent data-testid="promote-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Promote to super-admin?</AlertDialogTitle>
            <AlertDialogDescription>
              This grants <strong>{promoteTarget?.email}</strong> full admin access — including this user management console,
              billing and every customer account. This cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmPromote} disabled={busy} data-testid="promote-confirm">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Promote"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this user permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>{deleteTarget?.email}</strong> and all of their connections, governance rules,
              extractions, presets and saved queries. This <strong>cannot be undone</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={busy}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
